import { useEffect, useRef } from "react";
import { gsap } from "@/animations/gsap";
import { createExperienceTimeline } from "@/animations/timeline";
import { createFadeUp } from "@/animations/reveal";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { experience } from "@/data/experience";

export function Experience() {
  const ref = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const ctx = gsap.context(() => {
      createFadeUp(".exp-header", ref.current!);
      createExperienceTimeline(ref.current!);
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section id="experience" ref={ref} className="container-x section-y" data-testid="experience-section">
      <div className="exp-header">
        <p className="eyebrow">04 — EXPERIENCE</p>
        <h2 className="font-display mt-8 text-[clamp(2.5rem,6vw,5.5rem)] font-semibold uppercase leading-[0.98] tracking-tight">
          THE <span className="text-outline">PATH</span> SO FAR
        </h2>
        <p className="mt-6 max-w-xl font-mono text-xs leading-relaxed tracking-wider text-muted">
          PERIODS AND COMPANIES ARE [PLACEHOLDER] VALUES — UPDATE IN DATA/EXPERIENCE.TS
        </p>
      </div>

      <div className="relative mt-20 pl-8 md:pl-16">
        <div className="absolute bottom-0 left-0 top-0 w-px bg-line" aria-hidden="true" />
        <div
          className="exp-line absolute bottom-0 left-0 top-0 w-px"
          style={{ backgroundColor: "var(--accent)", transform: "scaleY(0)", transformOrigin: "top" }}
          aria-hidden="true"
        />
        <ol className="grid gap-16">
          {experience.map((entry, i) => (
            <li key={`${entry.role}-${i}`} className="exp-item relative" data-testid={`experience-item-${i + 1}`}>
              <span className="exp-dot absolute -left-8 top-2 md:-left-16" style={{ marginLeft: -5 }} aria-hidden="true" />
              <article className="exp-card max-w-2xl border border-line bg-surface/60 p-8 transition-colors duration-500 hover:border-[var(--accent-muted)]">
                <p className="font-mono text-[10px] tracking-[0.25em] text-muted">{entry.period}</p>
                <h3 className="exp-role font-display mt-4 text-2xl font-semibold tracking-tight md:text-3xl">{entry.role}</h3>
                <p className="mt-1 font-mono text-xs tracking-[0.2em] text-accent">{entry.company}</p>
                <p className="mt-5 text-sm leading-relaxed text-muted">{entry.description}</p>
              </article>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
